import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FaSearch } from "react-icons/fa";
import { getActionTitle } from "../../helpers/header-action";
import "./NodeSearch.css";

/**
 * Busca de nós (tickets 02 + 10 — .scratch/builder-ux).
 *
 * Entrada provisória (trigger flutuante + Ctrl/Cmd+K) até a topbar nova (04)
 * hospedar o campo. Ao selecionar um resultado, o pai (Flow) centraliza a tela
 * no nó com animação (setCenter) e dá um "pulse" de destaque.
 *
 * Match:
 * - Primário: Ref (id) + nome do evento (getActionTitle).
 * - Conteúdo interno (ticket 10): todos os campos free-text da ação, incl.
 *   técnicos (webhook url/body/headers, ids do Gush, variáveis). O índice
 *   {node -> texto normalizado} é memoizado por `nodes` — recomputa só quando
 *   o fluxo muda, não a cada tecla.
 *
 * Ranking: matches de Ref/nome vêm antes de matches só-de-conteúdo. No preview,
 * quando o casamento for no conteúdo, mostramos o trecho com o termo destacado.
 */

const MAX_RESULTS = 50;
const SNIPPET_RADIUS = 40;

// Campos de conteúdo interno que entram no match (decisão do ticket 10:
// "tudo, incl. campos técnicos"). Composições (arrays/objetos) são achatadas
// pelo collectStrings abaixo.
const CONTENT_FIELDS = [
	"msgMessage", // HTML — stripado
	"question",
	"questionButtons",
	"questionList",
	"comment",
	"condVar",
	"condValue",
	"jumps",
	"jumpVar",
	"varToSet",
	"valueToset",
	"webhookUrl",
	"webhookBody", // HTML/JSON — stripado
	"webhookHeaders",
	"aiAgentName",
	"aiInputVariables",
	"aiOutputs",
	"gushSector",
	"gushInternalId",
	"tags",
];

function stripHtml(value) {
	return String(value || "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

// Achata qualquer valor (string/número/array/objeto) em strings, ignorando
// booleanos e nulos. Cobre as composições (questionButtons[], questionList
// .sections[], aiInputVariables[], webhookHeaders[], jumps{}) sem hardcode.
function collectStrings(value, out) {
	if (value == null) return;
	const t = typeof value;
	if (t === "string") {
		if (value) out.push(value);
	} else if (t === "number") {
		out.push(String(value));
	} else if (Array.isArray(value)) {
		for (const v of value) collectStrings(v, out);
	} else if (t === "object") {
		for (const k of Object.keys(value)) collectStrings(value[k], out);
	}
}

// Texto de conteúdo interno normalizado (HTML stripado) para o índice de busca.
function getNodeContentText(node) {
	const d = node.data || {};
	const parts = [];
	for (const key of CONTENT_FIELDS) {
		if (d[key] != null) collectStrings(d[key], parts);
	}
	if (!parts.length) return "";
	return stripHtml(parts.join(" · "));
}

// Preview genérico (primeiro campo não-vazio) — usado quando o match foi em
// Ref/nome e não há trecho de conteúdo para destacar.
function getNodePreview(node) {
	const d = node.data || {};
	const raw =
		d.msgMessage ||
		d.question ||
		d.comment ||
		d.condValue ||
		d.webhookUrl ||
		d.valueToset ||
		d.aiAgentName ||
		"";
	return stripHtml(raw).slice(0, 90);
}

// Recorta uma janela de `content` ao redor do 1º casamento de `q` (já em
// minúsculas), devolvendo as partes p/ destacar. `null` se não casar.
function buildSnippet(content, q) {
	if (!content || !q) return null;
	const idx = content.toLowerCase().indexOf(q);
	if (idx < 0) return null;
	const start = Math.max(0, idx - SNIPPET_RADIUS);
	const end = Math.min(content.length, idx + q.length + SNIPPET_RADIUS);
	return {
		before: (start > 0 ? "…" : "") + content.slice(start, idx),
		match: content.slice(idx, idx + q.length),
		after: content.slice(idx + q.length, end) + (end < content.length ? "…" : ""),
	};
}

function getNodeRef(node) {
	return String(node.data?.ref || node.id || "");
}

export default function NodeSearch({
	nodes,
	onSelect,
	inline = false,
	className = "",
}) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [activeIndex, setActiveIndex] = useState(0);
	// Recentes: ids dos nós que o usuário abriu pela busca. Estado em memória —
	// sobrevive a fechar/reabrir a busca, zera no reload (sessão-only, ticket 11).
	const [recents, setRecents] = useState([]);
	const inputRef = useRef(null);

	// Índice memoizado por `nodes`: só recomputa quando o fluxo muda, não a cada
	// tecla. Guarda texto original (p/ o snippet) e minúsculo (p/ o match).
	const index = useMemo(() => {
		return nodes.map((node) => {
			const ref = getNodeRef(node);
			const title = getActionTitle(node.type) || node.type || "";
			const content = getNodeContentText(node);
			return {
				node,
				ref,
				title,
				refLower: ref.toLowerCase(),
				titleLower: title.toLowerCase(),
				content,
				contentLower: content.toLowerCase(),
			};
		});
	}, [nodes]);

	// Ranking: Ref/nome primeiro, conteúdo-só depois. Cap de MAX_RESULTS.
	const { results, totalMatches } = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return { results: [], totalMatches: 0 };
		const primary = [];
		const contentOnly = [];
		for (const entry of index) {
			if (entry.refLower.includes(q) || entry.titleLower.includes(q)) {
				primary.push(entry);
			} else if (entry.contentLower.includes(q)) {
				contentOnly.push(entry);
			}
		}
		const total = primary.length + contentOnly.length;
		return {
			results: primary.concat(contentOnly).slice(0, MAX_RESULTS),
			totalMatches: total,
		};
	}, [query, index]);

	// Nós recentes existentes (ids órfãos — de outro bot/apagados — são ignorados).
	const nodeById = useMemo(() => {
		const m = new Map();
		for (const n of nodes) m.set(n.id, n);
		return m;
	}, [nodes]);
	const recentNodes = useMemo(
		() => recents.map((id) => nodeById.get(id)).filter(Boolean),
		[recents, nodeById]
	);

	// Lista sob navegação por teclado: resultados quando há filtro, recentes quando vazio.
	const navNodes = query.trim() ? results.map((r) => r.node) : recentNodes;

	const openPanel = useCallback(() => {
		setOpen(true);
	}, []);

	// Fecha mantendo o filtro (persistência na sessão, ticket 11).
	const closePanel = useCallback(() => {
		setOpen(false);
		setActiveIndex(0);
	}, []);

	const clearQuery = useCallback(() => {
		setQuery("");
		setActiveIndex(0);
		inputRef.current?.focus();
	}, []);

	const choose = useCallback(
		(node) => {
			if (!node) return;
			setRecents((prev) =>
				[node.id, ...prev.filter((id) => id !== node.id)].slice(0, 8)
			);
			onSelect?.(node);
			closePanel();
		},
		[onSelect, closePanel]
	);

	// Ctrl/Cmd+K abre; Esc fecha. Não abre quando o foco está em campo de texto.
	useEffect(() => {
		const handler = (e) => {
			const key = (e.key || "").toLowerCase();
			if ((e.ctrlKey || e.metaKey) && key === "k") {
				const el = document.activeElement;
				const tag = el?.tagName;
				const typing =
					tag === "INPUT" ||
					tag === "TEXTAREA" ||
					tag === "SELECT" ||
					el?.isContentEditable;
				if (typing && !open) return;
				e.preventDefault();
				setOpen((prev) => !prev);
			} else if (key === "escape" && open) {
				closePanel();
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [open, closePanel]);

	useEffect(() => {
		if (open && inputRef.current) inputRef.current.focus();
	}, [open]);

	useEffect(() => {
		setActiveIndex(0);
	}, [query]);

	const onInputKeyDown = (e) => {
		if (e.key === "ArrowDown") {
			e.preventDefault();
			setActiveIndex((i) => Math.min(i + 1, navNodes.length - 1));
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			setActiveIndex((i) => Math.max(i - 1, 0));
		} else if (e.key === "Enter") {
			e.preventDefault();
			choose(navNodes[activeIndex]);
		}
	};

	const q = query.trim().toLowerCase();

	// Item de resultado/recente. `content` só é usado p/ destacar o trecho que
	// casou (vazio nos recentes → cai no preview genérico).
	const renderItem = (node, i, content, ref, title) => {
		const snippet = buildSnippet(content, q);
		return (
			<button
				type="button"
				key={node.id}
				className={
					"node-search__item" + (i === activeIndex ? " is-active" : "")
				}
				onMouseEnter={() => setActiveIndex(i)}
				onClick={() => choose(node)}
			>
				<div className="node-search__itemMain">
					<span className="node-search__itemTitle">{title || node.type}</span>
					{snippet ? (
						<span className="node-search__itemPreview">
							{snippet.before}
							<mark className="node-search__hl">{snippet.match}</mark>
							{snippet.after}
						</span>
					) : (
						getNodePreview(node) && (
							<span className="node-search__itemPreview">
								{getNodePreview(node)}
							</span>
						)
					)}
				</div>
				<span className="node-search__itemRef">{ref}</span>
			</button>
		);
	};

	return (
		<div
			className={
				"node-search" +
				(inline ? " node-search--inline" : "") +
				(className ? " " + className : "")
			}
		>
			<button
				type="button"
				className="node-search__trigger"
				title="Buscar ação (Ctrl/Cmd+K)"
				onClick={openPanel}
			>
				<FaSearch />
				<span>Buscar ação</span>
				<kbd>⌘K</kbd>
			</button>

			{open && (
				<>
					<div className="node-search__backdrop" onClick={closePanel} />
					<div className="node-search__panel" role="dialog" aria-label="Buscar ação">
						<div className="node-search__inputRow">
							<FaSearch className="node-search__inputIcon" />
							<input
								ref={inputRef}
								className="node-search__input"
								value={query}
								onChange={(e) => setQuery(e.target.value)}
								onKeyDown={onInputKeyDown}
								placeholder="Buscar por Ref, nome do evento ou conteúdo…"
							/>
							{query ? (
								<button
									type="button"
									className="node-search__clear"
									onClick={clearQuery}
									title="Limpar busca"
									aria-label="Limpar busca"
								>
									×
								</button>
							) : (
								<kbd>Esc</kbd>
							)}
						</div>

						<div className="node-search__results">
							{query.trim() ? (
								<>
									{results.length === 0 && (
										<div className="node-search__empty">
											Nenhuma ação encontrada
										</div>
									)}
									{results.map((entry, i) =>
										renderItem(entry.node, i, entry.content, entry.ref, entry.title)
									)}
									{totalMatches > results.length && (
										<div className="node-search__more">
											+{totalMatches - results.length} resultado(s) — refine a busca
										</div>
									)}
								</>
							) : recentNodes.length > 0 ? (
								<>
									<div className="node-search__sectionLabel">Recentes</div>
									{recentNodes.map((node, i) =>
										renderItem(
											node,
											i,
											"",
											getNodeRef(node),
											getActionTitle(node.type) || node.type
										)
									)}
								</>
							) : (
								<div className="node-search__empty">
									Digite para buscar por Ref, nome do evento ou conteúdo
								</div>
							)}
						</div>
					</div>
				</>
			)}
		</div>
	);
}
